import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';

/**
 * Updates the selected options based on the provided temporal IDs and filter options array.
 * If any temporal ID does not exist in the filter options array, it filters out invalid selection options
 * and invokes the provided callbacks with the updated array of valid IDs.
 *
 * @param temporalIds - An array of temporal IDs to be validated and updated.
 * @param filterOptionsArr - An array of filter options.
 * @param callback1 - A callback function to be invoked with the updated array of valid IDs.
 * @param callback2 - A second callback function to be invoked with the updated array of valid IDs.
 */
export const handleUpdatedOptionsWithSelectedOne = (
  temporalIds: unknown[],
  filterOptionsArr: Array<{ id: string | number }>,
  callback1: (arr: unknown[]) => void,
  callback2: (arr: unknown[]) => void,
) => {
  if (temporalIds.length && temporalIds.some((id) => !filterOptionsArr.some((wbOpt) => wbOpt.id === id))) {
    const newSelectedArr = temporalIds.filter((id) => filterOptionsArr.some((wbOpt) => wbOpt.id === id));

    callback1(newSelectedArr);
    callback2(newSelectedArr);
  }
};

/**
 * @description Returns an updated array of selections based on the provided temporal IDs and filter options array.
 * If any of the selections are not present in the filter options array, they are filtered out.
 * Otherwise, the original array of selections is returned.
 *
 * @param temporalIds - The current array of filter selections.
 * @param filterOptionsArr - An array of objects containing valid filter options properties.
 * @returns An array of selections representing the updated selection.
 */
export const getUpdatedOptions = (temporalIds: FilterIdsType, filterOptionsArr: Array<{ id: string | number }>) => {
  const isAnySelectionMissingInOptions = temporalIds.some((id) => !filterOptionsArr.some((option) => option.id === id));
  const shouldInvalidateFilterSelections = temporalIds.length && isAnySelectionMissingInOptions;

  return shouldInvalidateFilterSelections
    ? temporalIds.filter((id) => filterOptionsArr.some((option) => option.id === id))
    : temporalIds;
};
